import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  Button, CircularProgress, Chip, LinearProgress, Avatar,
  List, ListItem, ListItemText, ListItemAvatar, Divider
} from '@mui/material';
import {
  ChildCare as ChildIcon,
  EventNote as MeetingIcon,
  AccountBalance as FeeIcon,
  CheckCircle as PresentIcon,
  School as SchoolIcon,
  ArrowForward as ArrowIcon,
  Event as CalendarIcon,
  AssignmentLate as CareNoteIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { parentAPI } from '../../services/api';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const AttendanceBadge = ({ percentage }) => {
  if (percentage === null) return <Chip label="No data" size="small" />;
  const color = percentage >= 75 ? 'success' : percentage >= 60 ? 'warning' : 'error';
  return <Chip label={`${percentage}%`} color={color} size="small" />;
};

const FeeStatusChip = ({ pending }) => {
  if (pending <= 0) return <Chip label="All Clear" color="success" size="small" />;
  return <Chip label={`₹${pending.toLocaleString('en-IN')} due`} color="error" size="small" />;
};

const StatCard = ({ icon, label, value, color, onClick }) => (
  <Card
    elevation={2}
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      ...(onClick && { '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 } })
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ bgcolor: `${color}.light`, borderRadius: 2, p: 1.5, color: `${color}.dark` }}>
        {icon}
      </Box>
      <Box flex={1}>
        <Typography variant="h5" fontWeight={700}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
      {onClick && <ArrowIcon sx={{ color: 'text.disabled' }} />}
    </CardContent>
  </Card>
);

const ParentDashboard = () => {
  const { user, logout, isFeatureEnabled } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await parentAPI.getDashboard();
      setData(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load dashboard data.');
      console.error('Parent dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={8}><CircularProgress size={56} /></Box>
        ) : error ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button onClick={fetchDashboard} sx={{ mt: 2 }}>Retry</Button>
          </Paper>
        ) : (
          <>
            {/* Welcome */}
            <Box mb={4}>
              <Typography variant="h4" fontWeight={700}>
                Welcome, {user?.name || 'Parent'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Here's an overview of your {data?.stats?.totalChildren === 1 ? 'child' : 'children'}.
              </Typography>
            </Box>

            {/* Stats */}
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<ChildIcon />}
                  label="Children"
                  value={data?.stats?.totalChildren ?? 0}
                  color="primary"
                  onClick={() => document.getElementById('children-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<MeetingIcon />}
                  label="Upcoming Meetings"
                  value={data?.stats?.upcomingMeetings ?? 0}
                  color="warning"
                  onClick={() => navigate('/parent/meetings')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<FeeIcon />}
                  label="Total Fees Pending"
                  value={data?.stats?.totalPendingFees > 0
                    ? `₹${data.stats.totalPendingFees.toLocaleString('en-IN')}`
                    : 'Nil'}
                  color={data?.stats?.totalPendingFees > 0 ? 'error' : 'success'}
                  onClick={data?.children?.length === 1
                    ? () => navigate(`/parent/child/${data.children[0].id}`)
                    : () => document.getElementById('children-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
              </Grid>
            </Grid>

            {/* Children Cards */}
            <Typography id="children-section" variant="h6" fontWeight={600} mb={2}>Your Children</Typography>
            {(!data?.children || data.children.length === 0) ? (
              <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
                <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No children linked to your account yet.</Typography>
              </Paper>
            ) : (
              <Grid container spacing={3} mb={4}>
                {data.children.map((child) => (
                  <Grid item xs={12} sm={6} md={4} key={child.id}>
                    <Card
                      elevation={2}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        border: '2px solid transparent',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 8,
                          borderColor: 'primary.main',
                        }
                      }}
                      onClick={() => navigate(`/parent/child/${child.id}`)}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                            {child.name?.[0]?.toUpperCase() || 'S'}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>{child.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {child.admission_number}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" mb={1}>
                          <SchoolIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                          {child.class?.name || 'No class assigned'}
                        </Typography>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Attendance (30d)</Typography>
                            <Box>
                              <AttendanceBadge percentage={child.attendanceStats?.percentage} />
                            </Box>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="caption" color="text.secondary">Fees</Typography>
                            <Box>
                              <FeeStatusChip pending={child.feeStatus?.totalPending || 0} />
                            </Box>
                          </Box>
                        </Box>

                        {child.attendanceStats?.totalDays > 0 && (
                          <Box mt={1.5}>
                            <LinearProgress
                              variant="determinate"
                              value={child.attendanceStats.percentage || 0}
                              color={
                                child.attendanceStats.percentage >= 75 ? 'success' :
                                child.attendanceStats.percentage >= 60 ? 'warning' : 'error'
                              }
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        )}

                        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                          {isFeatureEnabled('student_documents') && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); navigate(`/parent/children/${child.id}/documents`); }}
                            >
                              Documents
                            </Button>
                          )}
                          <Button
                            endIcon={<ArrowIcon />}
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/parent/child/${child.id}`); }}
                          >
                            View Details
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Daycare — separate section for daycare-enrolled children only */}
            {(() => {
              const daycareChildren = data?.children?.filter(c => c.daycare_enrolled) || [];
              if (daycareChildren.length === 0) return null;
              const daycareColor = '#7c3aed';
              return (
                <Box
                  sx={{
                    p: 2.5,
                    mb: 4,
                    borderRadius: 3,
                    bgcolor: alpha(daycareColor, 0.06),
                    border: '1px dashed',
                    borderColor: alpha(daycareColor, 0.35),
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ChildIcon sx={{ color: daycareColor }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: daycareColor }}>
                      Daycare
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {daycareChildren.map(child => (
                      <Grid item xs={12} sm={6} md={4} key={child.id}>
                        <Card
                          elevation={0}
                          onClick={() => navigate(`/parent/child/${child.id}?tab=daycare`)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2,
                            color: '#fff',
                            background: `linear-gradient(135deg, ${daycareColor} 0%, #5b21b6 100%)`,
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 },
                          }}
                        >
                          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                              {child.name?.[0]?.toUpperCase() || 'S'}
                            </Avatar>
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight={700}>{child.name}</Typography>
                              <Typography variant="caption" sx={{ opacity: 0.85 }}>Daycare sessions & dues</Typography>
                            </Box>
                            <ArrowIcon />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                    {isFeatureEnabled('care_notes') && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Card
                          elevation={0}
                          onClick={() => navigate('/parent/care-notes')}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2,
                            color: '#fff',
                            background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 },
                          }}
                        >
                          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CareNoteIcon sx={{ fontSize: 32 }} />
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight={700}>Care Notes</Typography>
                              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                Send daily care instructions to the teacher
                              </Typography>
                            </Box>
                            <ArrowIcon />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })()}

            {/* Quick Links */}
            {isFeatureEnabled('academics') && (
              <Grid container spacing={2} mb={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    elevation={0}
                    onClick={() => navigate('/parent/calendar')}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      color: '#fff',
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-3px)', boxShadow: 6 },
                    }}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CalendarIcon sx={{ fontSize: 32 }} />
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight={700}>School Calendar</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                          Events, holidays & exam dates
                        </Typography>
                      </Box>
                      <ArrowIcon />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Upcoming Meetings */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>Upcoming Meetings</Typography>
              <Button
                endIcon={<ArrowIcon />}
                onClick={() => navigate('/parent/meetings')}
              >
                All Meetings
              </Button>
            </Box>
            <Paper elevation={1}>
              {(!data?.upcomingMeetings || data.upcomingMeetings.length === 0) ? (
                <Box p={3} textAlign="center">
                  <CalendarIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No upcoming meetings scheduled.</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {data.upcomingMeetings.map((meeting, idx) => (
                    <React.Fragment key={meeting.id}>
                      {idx > 0 && <Divider />}
                      <ListItem sx={{ py: 2 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}>
                            <MeetingIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle2">
                                {meeting.student?.name || 'Student'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                with {meeting.teacher?.user?.name || 'Teacher'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            `${formatDate(meeting.scheduled_datetime)} at ${formatTime(meeting.scheduled_datetime)}`
                          }
                        />
                        <Chip label="Scheduled" color="primary" size="small" />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </>
        )}
      </Container>
  );
};

export default ParentDashboard;
